import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Link, useOutletContext } from '@remix-run/react';
import { Loader2 } from 'lucide-react';
import PLusIcon from '~/assets/icons/PlusIcon';
import { SearchQuery } from '~/generated/graphql';
import { OutletContext } from '~/types';
import { Price } from './Price';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '../ui/dialog';
import { RichText } from './RichText';
import { PLACEHOLDER_IMAGE_URL } from '~/constants';

export type ProductCardProps = SearchQuery['search']['items'][number];

export function ProductCard({
  productAsset,
  productName,
  slug,
  priceWithTax,
  currencyCode,
  productVariantId,
  description,
}: ProductCardProps) {
  const { activeOrderFetcher } = useOutletContext<OutletContext>();
  const idBeingAddedToOrder = activeOrderFetcher?.formData?.get('variantId');

  return (
    <div className="relative border rounded-xl py-3 sm:py-4 lg:py-8 px-2.5 sm:px-4 lg:px-8 w-full border-gray-200 overflow-hidden">
      <Dialog>
        <DialogTrigger asChild>
          <button
            type="button"
            className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-200 transition z-10"
          >
            <MagnifyingGlassIcon className="w-5 h-5 text-gray-700" />
          </button>
        </DialogTrigger>

        <DialogContent className="w-full sm:max-w-lg bg-white p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>{productName}</DialogTitle>
            <DialogDescription>
              <Price priceWithTax={priceWithTax} currencyCode={currencyCode} />
            </DialogDescription>
          </DialogHeader>

          {productAsset?.preview && (
            <img
              src={
                productAsset.preview
                  ? productAsset.preview + '?w=600&h=500'
                  : PLACEHOLDER_IMAGE_URL
              }
              alt={productName}
              className="w-full h-auto max-h-[300px] object-cover rounded mb-4"
            />
          )}
          {description ? (
            <RichText
              html={description}
              className="text-gray-700 text-xs md:text-base"
            />
          ) : (
            <p className="text-gray-700">No description available.</p>
          )}
        </DialogContent>
      </Dialog>

      <Link
        prefetch="intent"
        to={`/products/${slug}`}
        className="flex flex-col items-center text-center mt-4"
      >
        {productAsset?.preview && (
          <img
            className="w-[96px] h-[88px] sm:w-[120px] sm:h-[104px] lg:w-[140px] lg:h-[120px] mx-auto object-cover pt-1 sm:pt-2 lg:pt-4 mb-2 rounded"
            alt={productName}
            src={productAsset?.preview + '?w=300&h=400'}
          />
        )}
        <h3 className="font-normal pt-1 sm:pt-2 lg:pt-4 text-black text-[11px] sm:text-xs lg:text-lg leading-snug line-clamp-2">
          {productName}
        </h3>
        <p className="text-gray-500 text-[11px] sm:text-xs lg:text-sm mt-1">
          <Price priceWithTax={priceWithTax} currencyCode={currencyCode} />
        </p>
      </Link>

      <div className="absolute bottom-0 right-0 w-10 h-10 bg-primary rounded-tl-full flex items-end justify-center p-2">
        <activeOrderFetcher.Form
          method="post"
          action="/api/active-order"
          className="absolute bottom-0 right-0 w-10 h-10 bg-primary rounded-tl-full flex items-center justify-center p-2"
        >
          <input type="hidden" name="variantId" value={productVariantId} />
          <input type="hidden" name="action" value="addItemToOrder" />
          <button
            type="submit"
            disabled={activeOrderFetcher.state !== 'idle'}
            className="flex items-center justify-center w-full h-full"
          >
            {idBeingAddedToOrder === productVariantId ? (
              <Loader2 className="text-white animate-spin" />
            ) : (
              <PLusIcon />
            )}
          </button>
        </activeOrderFetcher.Form>
      </div>
    </div>
  );
}
